/**
 * <spivex-sticky-cta>: buy bar at the bottom of the screen on phones. It appears once the main button of the section
 * (data-watch, a selector inside the section) has left the screen upwards and hides again while the button is visible
 * or while the target of the link (e.g. the package overview) is on screen, so it never doubles the real buttons.
 * Without JavaScript or IntersectionObserver the bar stays hidden; the button in the section is always there.
 *
 * 29.10.2026: Andocken (data-andock und ein Platz [data-spivex-andock] auf der Seite, z. B. über „Wähl dein Paket“):
 * Der Knopf schwebt von Anfang an unten. Kommt der Andockplatz an die Stelle des Knopfes, bleibt der Knopf dort
 * „stehen“ (eine Kopie im Andockplatz scrollt mit der Seite). Erst wenn das Ende der Pakete ([data-spivex-andock-ende],
 * Doppelpaket oder letztes Paket) über dem Knopf ist, schwebt er wieder. Den Knopf im Startbild braucht es am Handy
 * dann nicht mehr (Klasse spivex-hero--schwebend an der Section).
 *
 * 30.10.2026: Auch ab 750 px (Tablet, PC). Dort bleibt der Knopf im Startbild, es gibt keinen Andockplatz: Der Knopf
 * schwebt, sobald der im Startbild oben aus dem Bild ist, und pausiert, solange die Pakete zu sehen sind. Ab 990 px
 * scrollt nicht das Fenster, sondern .page-wrapper, deshalb horcht der Scroll-Listener in der Capture-Phase.
 */
class SpivexStickyCta extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #buttonAbove = false;
  #targetVisible = false;
  /** @type {HTMLElement | null} */
  #dock = null;
  /** @type {HTMLElement | null} */
  #end = null;
  /** @type {HTMLElement | null} */
  #heroButton = null;
  #frame = 0;
  #spot = 0;
  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  connectedCallback() {
    const dock = this.dataset.andock !== undefined ? document.querySelector('[data-spivex-andock]') : null;
    if (dock instanceof HTMLElement) {
      this.#startDock(dock);
      return;
    }

    if (!('IntersectionObserver' in window)) return;
    const section = this.closest('section') || document;
    const button = section.querySelector(this.dataset.watch || '');
    if (!button) return;

    const link = this.querySelector('a[href^="#"]');
    const target = link ? document.getElementById(decodeURIComponent(link.hash.slice(1))) : null;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === button) {
          this.#buttonAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        } else {
          this.#targetVisible = entry.isIntersecting;
        }
      }
      this.hidden = !(this.#buttonAbove && !this.#targetVisible);
    });
    this.#observer.observe(button);
    if (target) this.#observer.observe(target);
  }

  /** @param {HTMLElement} dock */
  #startDock(dock) {
    const link = this.querySelector('a');
    if (!link) return;
    if (!dock.querySelector('a')) {
      const copy = /** @type {HTMLElement} */ (link.cloneNode(true));
      dock.append(copy);
    }
    this.#dock = dock;
    const end = document.querySelector('[data-spivex-andock-ende]');
    this.#end = end instanceof HTMLElement ? end : null;
    const section = this.closest('section');
    section?.classList.add('spivex-hero--schwebend');
    const heroButton = this.dataset.watch ? section?.querySelector(this.dataset.watch) : null;
    this.#heroButton = heroButton instanceof HTMLElement ? heroButton : null;
    window.addEventListener('scroll', this.#onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.#onScroll);
    this.#update();
  }

  /** Floating or docked, from where the dock place and the end of the packages are right now. */
  #update() {
    if (!this.#dock) return;
    const height = window.innerHeight;
    if (!this.hidden) {
      const top = this.getBoundingClientRect().top;
      if (top > 0) this.#spot = top;
    }
    const dockButton = this.#dock.querySelector('a');
    const buttonHeight = dockButton?.offsetHeight || 52;
    const spot = this.#spot || height - buttonHeight - 12;

    // Ab 750 px ist der Andockplatz ausgeblendet: Maßstab sind die Pakete direkt dahinter und der Knopf im Startbild.
    const wide = window.matchMedia('(min-width: 750px)').matches;
    const dockArea = (wide && this.#dock.nextElementSibling) || this.#dock;
    const beforeDock = dockArea.getBoundingClientRect().top > spot;
    const headerBottom = document.querySelector('#header-component')?.getBoundingClientRect().bottom ?? 0;
    const heroButtonGone = !wide || !this.#heroButton || this.#heroButton.getBoundingClientRect().bottom < headerBottom;
    const afterEnd = this.#end ? this.#end.getBoundingClientRect().bottom < spot - 16 : false;
    this.hidden = !((beforeDock && heroButtonGone) || afterEnd);
    // Die Kopie im Andockplatz erst zeigen, wenn der Knopf dort angekommen ist, nie doppelt.
    this.#dock.toggleAttribute('data-angedockt', !beforeDock);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    window.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.removeEventListener('resize', this.#onScroll);
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('spivex-sticky-cta')) customElements.define('spivex-sticky-cta', SpivexStickyCta);
